import "./Sidebar.css";
import logo from "../../assets/Prople Logo.jpg";
import { ReactComponent as HomeIcon } from "../../assets/home.svg";
import { ReactComponent as HomeWhiteIcon } from "../../assets/Home-white.svg";
import { ReactComponent as RequestIcon } from "../../assets/Request.svg";
import { ReactComponent as RequestWhiteIcon } from "../../assets/RequestWhite.svg";
import { ReactComponent as SettingIcon } from "../../assets/setting.svg";
import { ReactComponent as ActivityIcon } from "../../assets/ActivityLog.svg";
import { ReactComponent as ActivityWhiteIcon } from "../../assets/WhiteActivityLog.svg";
import { ReactComponent as ClientCustomer } from "../../assets/ServiceFee.svg";
import { ReactComponent as ServiceFeeWhite } from "../../assets/ServiceFeeWhite.svg";
import { ReactComponent as ClientList } from "../../assets/ClientList.svg";
import { ReactComponent as ClientListWhiteIcon } from "../../assets/ClientListWhite.svg";
import SettingWhiteIcon from "../../assets/SettingWhite.png";
import { MailOutlined, WarningOutlined, MessageOutlined } from "@ant-design/icons";
import { Badge, notification } from "antd";
import { useRef } from "react";
import { TeamOutlined, BarChartOutlined, FileTextOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { decryptObject } from "../GlobalFunction";
import { getMyPermissions, getTicketCount, getNewTickets } from "../../services/ApiService";
import { playNotificationSound } from "../../utils/notificationSound";
import { TICKET_COUNT_CHANGED_EVENT } from "../../utils/ticketEvents";

// How often to poll the open-ticket count for the Inbox badge -- matches
// the Header bell's notification-count polling interval, same tradeoff
// (cheap COUNT query, frequent enough to feel live without hammering it).
const TICKET_COUNT_POLL_MS = 15000;

// sessionStorage key for the highest ticket id already toasted -- plain
// useRef state wouldn't survive Sidebar remounting on every page
// navigation (confirmed existing behavior, see permissions caching just
// below), so the "new email" toast would silently re-baseline (and skip
// a poll's worth of detection) on every navigation without this.
const LAST_SEEN_TICKET_ID_KEY = "lastSeenTicketId";
const LAST_SEEN_REPLY_ID_KEY = "lastSeenReplyId";

const readCachedPermissions = () => {
  try {
    const raw = sessionStorage.getItem("permissions");
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

const Sidebar = ({ activeId }) => {
  const navigate = useNavigate();
  const userType = decryptObject(
    sessionStorage.getItem("sessioninfo")
  ).userType;

  // Seeded from cache so the menu renders instantly (no blank flash), then
  // refreshed on every mount (i.e. every page navigation, since Sidebar
  // remounts per page) so a role change made while this user is already
  // logged in shows up without needing a fresh login.
  const [permissions, setPermissions] = useState(readCachedPermissions);

  useEffect(() => {
    let cancelled = false;
    const refreshPermissions = async () => {
      try {
        const res = await getMyPermissions();
        if (!cancelled && res?.responseCode === 200) {
          sessionStorage.setItem(
            "permissions",
            JSON.stringify(res.data.permissions)
          );
          setPermissions(res.data.permissions);
        }
      } catch (err) {
        console.error("Failed to refresh permissions", err);
      }
    };
    refreshPermissions();
    return () => {
      cancelled = true;
    };
  }, []);

  // "New" = open tickets (nobody has started on them yet) -- see
  // get_ticket_count on the backend. Only matters once the Inbox item is
  // actually visible, but fetching it unconditionally is cheap and
  // avoids the item popping in badge-less then flashing a number in.
  const [ticketCount, setTicketCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchTicketCount = async () => {
      try {
        const res = await getTicketCount();
        if (!cancelled && res?.responseCode === 200) {
          setTicketCount(res.count || 0);
        }
      } catch (err) {
        console.error("Failed to fetch ticket count", err);
      }
    };
    fetchTicketCount();
    const interval = setInterval(fetchTicketCount, TICKET_COUNT_POLL_MS);

    // Immediate refetch on top of the poll -- Inbox.js dispatches this
    // the moment a status change actually succeeds, so the badge doesn't
    // sit stale for up to TICKET_COUNT_POLL_MS waiting for the next tick.
    window.addEventListener(TICKET_COUNT_CHANGED_EVENT, fetchTicketCount);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener(TICKET_COUNT_CHANGED_EVENT, fetchTicketCount);
    };
  }, []);

  // Global "new email arrived" toast -- confirmed with the user,
  // 2026-10-29: more visible than the quiet badge-count bump, and shows
  // up on whichever page the user is actually on, not just the Inbox.
  // lastSeenTicketId lives in a ref (not state) purely so the poll
  // closure always reads the latest value without needing to be in this
  // effect's dependency array (which would otherwise tear down and
  // rebuild the interval on every single new ticket).
  const lastSeenTicketIdRef = useRef(
    Number(sessionStorage.getItem(LAST_SEEN_TICKET_ID_KEY)) || 0
  );
  // Same idea, for a reply landing on an EXISTING ticket's thread --
  // confirmed real gap, 2026-10-29: a reply never creates a new Ticket
  // row, so without tracking TicketInboundMessage ids as their own
  // series, a reply arriving while the requester is elsewhere in the
  // app never toasted at all.
  const lastSeenReplyIdRef = useRef(
    Number(sessionStorage.getItem(LAST_SEEN_REPLY_ID_KEY)) || 0
  );

  useEffect(() => {
    let cancelled = false;
    const pollNewTickets = async () => {
      try {
        const res = await getNewTickets(lastSeenTicketIdRef.current, lastSeenReplyIdRef.current);
        if (cancelled || res?.responseCode !== 200) return;

        // Once per poll batch, not once per item -- several new emails/
        // replies landing in the same 15s window shouldn't fire
        // overlapping chimes, confirmed with the user, 2026-10-29.
        if ((res.new_tickets || []).length > 0 || (res.new_replies || []).length > 0) {
          playNotificationSound();
        }

        (res.new_tickets || []).forEach((t) => {
          notification.open({
            message: "New email received",
            description: `${t.sender_name || t.sender_email}${
              t.client_name ? ` (${t.client_name})` : ""
            } — ${t.subject}`,
            icon:
              t.urgency === "high" ? (
                <WarningOutlined style={{ color: "#ff4d4f" }} />
              ) : (
                <MailOutlined style={{ color: "#534ECC" }} />
              ),
            placement: "topRight",
            duration: 8,
            onClick: () => navigate(`/inbox?ticket_id=${t.id}`),
          });
        });

        (res.new_replies || []).forEach((r) => {
          notification.open({
            message: "New reply received",
            description: `${r.sender_name || r.sender_email}${
              r.client_name ? ` (${r.client_name})` : ""
            } replied — ${r.subject}`,
            icon:
              r.urgency === "high" ? (
                <WarningOutlined style={{ color: "#ff4d4f" }} />
              ) : (
                <MessageOutlined style={{ color: "#534ECC" }} />
              ),
            placement: "topRight",
            duration: 8,
            onClick: () => navigate(`/inbox?ticket_id=${r.ticket_id}`),
          });
        });

        if (res.latest_id) {
          lastSeenTicketIdRef.current = res.latest_id;
          sessionStorage.setItem(LAST_SEEN_TICKET_ID_KEY, String(res.latest_id));
        }
        if (res.latest_reply_id) {
          lastSeenReplyIdRef.current = res.latest_reply_id;
          sessionStorage.setItem(LAST_SEEN_REPLY_ID_KEY, String(res.latest_reply_id));
        }
      } catch (err) {
        console.error("Failed to poll new tickets", err);
      }
    };
    pollNewTickets();
    const interval = setInterval(pollNewTickets, TICKET_COUNT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `module` maps an item to the Roles permission grid (constants.modules.MODULES
  // on the backend) -- an item shows only if the logged-in role has "show" on
  // that module. Items with no `module` (Home, Settings) aren't gated at all,
  // same as the backend doesn't gate them either.
  const menuConfig = [
    {
      id: 1,
      label: "Home",
      // CSP (userType 3) now shares the same richer Dashboard.js as
      // Admin/Manager -- confirmed with the user, 2026-10-29 -- rather
      // than the old sparse OPSDashboard.js ("/dashboard-e", left
      // registered but unreachable from here now, not deleted).
      // Dashboard.js already branches internally on userType === "1" to
      // scope everything to "my portfolio" for non-admins, so this is
      // just a routing change, not a new dashboard.
      path: userType === 4 ? "/dashboard-f" : "/dashboard",
      icon: <HomeIcon height={24} />,
      activeIcon: <HomeWhiteIcon height={24} />,
      visibleFor: [1, 2, 3, 4],
    },
    {
      id: 7,
      label: "Client List",
      path: "/clientList",
      icon: <ClientList height={24} />,
      activeIcon: <ClientListWhiteIcon height={24} />,
      visibleFor: [1, 4],
      module: "client_list",
    },
    {
      id: 10,
      label: "Customer List",
      path: "/clientCustomer",
      icon: <ClientList height={24} />,
      activeIcon: <ClientListWhiteIcon height={24} />,
      visibleFor: [1],
      module: "customer_list",
    },
    {
      id: 3,
      label: "Directory",
      path: "/masterDatabase",
      icon: <TeamOutlined style={{ fontSize: "1.4rem", color: "#787486" }} />,
      activeIcon: <TeamOutlined style={{ fontSize: "1.4rem" }} />,
      visibleFor: [1, 2, 3],
      module: "directory",
    },
    {
      id: 14,
      label: "Reports",
      path: "/reports",
      icon: <BarChartOutlined style={{ fontSize: "1.4rem", color: "#787486" }} />,
      activeIcon: <BarChartOutlined style={{ fontSize: "1.4rem" }} />,
      // Any user on the app can reach Reports, confirmed with the user,
      // 2026-10-28 -- same "visible to everyone" default as Home/Activity
      // Log, gated purely by the "reports" module permission once an
      // Admin grants it per role via the Roles page (same as every other
      // module-gated item here). Positioned right below Directory,
      // confirmed with the user, 2026-10-29.
      visibleFor: [1, 2, 3, 4],
      module: "reports",
    },
    {
      id: 15,
      label: "MCL Generator",
      path: "/mcl-generator",
      icon: <FileTextOutlined style={{ fontSize: "1.4rem", color: "#787486" }} />,
      activeIcon: <FileTextOutlined style={{ fontSize: "1.4rem" }} />,
      // Gated by the "mcl_generator" module permission, granted to Admin
      // and EOR Payroll by scripts/add_mcl_generator_module.py -- narrow
      // tool for the payroll team's Pag-IBIG/HDMF filing, confirmed with
      // the user, 2026-10-30.
      visibleFor: [1, 5],
      module: "mcl_generator",
    },
    // {
    //   id: 9,
    //   label: "All Records",
    //   path: "/allRecords",
    //   icon: <RequestIcon height={24} />,
    //   activeIcon: <RequestWhiteIcon height={24} />,
    //   visibleFor: [1],
    // },
    {
      id: 13,
      label: "Inbox",
      path: "/inbox",
      icon: <MailOutlined style={{ fontSize: "1.4rem", color: "#787486" }} />,
      activeIcon: <MailOutlined style={{ fontSize: "1.4rem" }} />,
      visibleFor: [1],
      module: "inbox",
    },
    {
      id: 5,
      label: "Activity Log",
      path: "/activityLog",
      icon: <ActivityIcon height={24} />,
      activeIcon: <ActivityWhiteIcon height={24} />,
      visibleFor: [1, 2, 3, 4],
      module: "activity_log",
    },
    {
      id: 4,
      label: "Settings",
      path: "/settings",
      icon: <SettingIcon height={24} />,
      activeIcon: <img src={SettingWhiteIcon} height={24} alt="Setting" />,
      visibleFor: [1, 2, 3, 4],
    },
    {
      id: 6,
      label: "Fields",
      path: "/fields",
      icon: <SettingIcon height={24} />,
      activeIcon: <img src={SettingWhiteIcon} height={24} alt="Setting" />,
      visibleFor: [1],
      module: "fields",
    },
    {
      id: 8,
      label: "CountryList",
      path: "/countryList",
      icon: <SettingIcon height={24} />,
      activeIcon: <img src={SettingWhiteIcon} height={24} alt="Setting" />,
      visibleFor: [1],
      module: "country_list",
    },
    {
      id: 11,
      label: "Roles",
      path: "/roles",
      icon: <SettingIcon height={24} />,
      activeIcon: <img src={SettingWhiteIcon} height={24} alt="Setting" />,
      visibleFor: [1],
      module: "roles",
    },
    {
      id: 12,
      label: "Users",
      path: "/users",
      icon: <TeamOutlined style={{ fontSize: "1.4rem", color: "#787486" }} />,
      activeIcon: <TeamOutlined style={{ fontSize: "1.4rem" }} />,
      visibleFor: [1],
      module: "users",
    },
  ];

  const filteredMenu = menuConfig.filter((item) => {
    if (!item.module) return true; // Home/Settings -- not permission-gated
    if (permissions) {
      return (permissions[item.module] || []).includes("show");
    }
    // Fallback for a session cached before this change (no "permissions" key
    // yet) -- old behavior, until the user logs in again.
    return item.visibleFor.includes(userType) || userType === 1;
  });

  const handleNavigate = (path) => navigate(path);

  return (
    <div className="sidebar">
      <div
        className="logo"
        onClick={() =>
          handleNavigate(
            userType === 2 || userType === 3
              ? "/dashboard"
              : userType === 4
              ? "/dashboard-f"
              : "/dashboard-admin"
          )
        }
      >
        <img src={logo} alt="logo" />
      </div>

      <div className="menu">
        {filteredMenu.map((item) => (
          <div
            key={item.id}
            className={`menu-item ${activeId === item.id ? "activeItem" : ""}`}
            onClick={() => handleNavigate(item.path)}
          >
            {activeId === item.id ? item.activeIcon : item.icon}
            <span className="menu-title">{item.label}</span>
            {item.module === "inbox" && ticketCount > 0 && (
              // Real count, not antd's default-99 cap -- confirmed with
              // the user, 2026-10-29: this is an operational queue people
              // act on, not a casual activity indicator, so "99+" hides
              // information that actually matters (47 vs. 134 open
              // tickets reads very differently). overflowCount still caps
              // it at 999 for the genuinely extreme case.
              <Badge
                count={ticketCount}
                overflowCount={999}
                size="small"
                style={{ marginLeft: "auto" }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Sidebar;
